/**
 * Image processing utilities for avatar uploads
 */

/**
 * Square crop an image to canvas with specified max size
 * @param {File} file - Image file to crop
 * @param {number} maxSize - Maximum dimension for the square canvas
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function squareCropToCanvas(file, maxSize) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate dimensions for center square crop
      const size = Math.min(img.width, img.height);
      const x = (img.width - size) / 2;
      const y = (img.height - size) / 2;

      // Create canvas at target size
      const canvas = document.createElement('canvas');
      canvas.width = maxSize;
      canvas.height = maxSize;
      const ctx = canvas.getContext('2d');

      // Draw center-cropped square, scaled to maxSize
      ctx.drawImage(
        img,
        x, y, size, size,  // source rectangle
        0, 0, maxSize, maxSize  // destination rectangle
      );

      resolve(canvas);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Convert canvas to File
 * @param {HTMLCanvasElement} canvas
 * @param {string} mime - MIME type (default: 'image/webp')
 * @param {number} quality - Quality (0-1, default: 0.85)
 * @returns {Promise<File>}
 */
export async function canvasToFile(canvas, mime = 'image/webp', quality = 0.85) {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const file = new File([blob], 'avatar.webp', { type: mime });
        resolve(file);
      },
      mime,
      quality
    );
  });
}

/**
 * Convert canvas to data URL
 * @param {HTMLCanvasElement} canvas
 * @param {string} mime - MIME type (default: 'image/webp')
 * @param {number} quality - Quality (0-1, default: 0.85)
 * @returns {Promise<string>}
 */
export async function dataUrlFromCanvas(canvas, mime = 'image/webp', quality = 0.85) {
  return new Promise((resolve) => {
    const dataUrl = canvas.toDataURL(mime, quality);
    resolve(dataUrl);
  });
}
